import { useTranslation } from '@silospot/i18n/react';
import { LOCALES, LOCALE_LABELS, type Locale } from '@silospot/shared';
import { cn } from '@silospot/ui/lib/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Bell,
  ChevronDown,
  Globe,
  GraduationCap,
  LayoutGrid,
  LifeBuoy,
  ListChecks,
  type LucideIcon,
  Map as MapIcon,
  Megaphone,
  Menu,
  Plus,
  Scale,
  ScrollText,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { api } from '../../lib/api';
import { useChangeLocale } from '../../lib/locale';
import { useAuthStore } from '../../stores/auth-store';
import { useLocaleStore } from '../../stores/locale-store';
import { usePeriodStore } from '../../stores/period-store';
import { useProjectStore } from '../../stores/project-store';
import { LockedDashboard } from './locked-dashboard';
import { NoTracker } from './no-tracker';
import { OnboardingWizard } from './onboarding-wizard';

interface NavItem {
  key: string;
  icon: LucideIcon;
  sub?: string;
  to?: string;
  live?: boolean;
}
const NAV: { group: string; items: NavItem[]; gated?: 'googleAds' }[] = [
  {
    group: 'analysis',
    items: [
      { key: 'overview', icon: LayoutGrid, sub: '' },
      { key: 'realtime', icon: Activity, sub: 'realtime', live: true },
      { key: 'audience', icon: Users, sub: 'audience' },
      { key: 'conversions', icon: Target, sub: 'conversions' },
      { key: 'geo', icon: MapIcon, sub: 'geo' },
    ],
  },
  {
    group: 'security',
    items: [
      { key: 'fraud', icon: ShieldAlert, sub: 'fraud' },
      { key: 'ip', icon: Globe, sub: 'visits-ip' },
      { key: 'review', icon: GraduationCap, sub: 'review' },
      { key: 'log', icon: ScrollText, sub: 'log' },
    ],
  },
  // Adaptive: only shown once Google Ads is connected.
  {
    group: 'advertising',
    gated: 'googleAds',
    items: [
      { key: 'adsCampaigns', icon: Megaphone, sub: 'ads/campaigns' },
      { key: 'adsAction', icon: ListChecks, sub: 'ads/action-center' },
      { key: 'adsClaims', icon: Scale, sub: 'ads/claims' },
      { key: 'adsRoi', icon: TrendingUp, sub: 'ads/roi' },
    ],
  },
  { group: 'management', items: [{ key: 'settings', icon: Settings, sub: 'settings' }] },
  { group: 'help', items: [{ key: 'support', icon: LifeBuoy, to: '/support' }] },
];

const PERIODS = ['today', 'yesterday', '7d', '14d', '30d', '1y', 'custom'] as const;

export function Shell(): ReactElement {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeProjectId } = useParams();
  const user = useAuthStore((s) => s.user);

  const { currentProjectId, setCurrentProjectId } = useProjectStore();
  // Single source of truth for "tracker installed": the project list's
  // `trackerPosted` flag (beacon ∪ Cloudflare Zaraz ∪ GTM ∪ manual snippet detected).
  // Poll while any site is not posted yet so the per-site gate clears as soon as the
  // tracker is in place — without waiting for a real visitor.
  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    refetchInterval: (q) =>
      (q.state.data?.projects ?? []).some((p) => p.trackerPosted === false) ? 8000 : false,
  });
  const gadsStatus = useQuery({ queryKey: ['gads-status'], queryFn: () => api.googleAdsStatus() });
  const gadsConnected = !!gadsStatus.data?.connected;
  const nav = NAV.filter((g) => !g.gated || (g.gated === 'googleAds' && gadsConnected));
  const projects = projectsQuery.data?.projects ?? [];
  const activeId = routeProjectId ?? currentProjectId ?? projects[0]?.id ?? '';
  const activeProject = projects.find((p) => p.id === activeId);

  // Per-site tracker gate: the analytics/security views of a site with no tracker
  // yet must show an "install the tracker" state, not silent zeros. Settings,
  // the Cloudflare setup and the advertising pages stay reachable. The org-level
  // onboarding lock above still covers the very first site.
  const siteSub = routeProjectId
    ? ((location.pathname.split(`/projects/${routeProjectId}`)[1] ?? '')
        .replace(/^\//, '')
        .split('/')[0] ?? '')
    : '';
  const ALWAYS_REACHABLE = new Set(['settings', 'cloudflare', 'ads']);
  // Show "install the tracker" only when it is NOT posted yet. Once posted (CF/GTM/
  // manual), the overview opens to its empty "awaiting traffic" state — never the
  // misleading "no tracker". The first beacon ("verified") is a separate indicator.
  const needsTracker =
    !!routeProjectId &&
    !ALWAYS_REACHABLE.has(siteSub) &&
    !!activeProject &&
    activeProject.trackerPosted === false;

  const [drawer, setDrawer] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  // The site the wizard must resume on after an OAuth round-trip (?wiz=<id>) — set
  // by the tracker/Ads connect returnTo so the wizard reopens on the RIGHT site,
  // not the oldest incomplete one.
  const [wizTarget, setWizTarget] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Onboarding lock (C-fix2): the dashboard is locked until one site is fully
  // configured. The wizard auto-opens once when locked (closable); the banner
  // and every "add a site" / integration entry point reopen it at the right step.
  const qc = useQueryClient();
  const onbQuery = useQuery({ queryKey: ['onboarding'], queryFn: () => api.getOnboarding() });
  // Lock the dashboard whenever there is no site at all (list loaded + empty) —
  // independent of the onboarding query, which briefly keeps its previous
  // "unlocked" value while it refetches after the last site is deleted. Without
  // this, the Outlet renders the home redirect on an empty list (a blank screen)
  // instead of the add-a-site wizard.
  const noSites = !projectsQuery.isLoading && projects.length === 0;
  const locked = noSites || !!onbQuery.data?.locked;
  // Resume on the wizard's own site when returning from OAuth (?wiz), else the
  // oldest incomplete site (the default banner/lock behaviour).
  const wizResumeQ = useQuery({
    queryKey: ['onboarding', 'wiz', wizTarget],
    queryFn: () => api.getOnboarding(wizTarget ?? undefined),
    enabled: !!wizTarget,
  });
  const resume = wizTarget ? (wizResumeQ.data?.resume ?? null) : (onbQuery.data?.resume ?? null);
  useEffect(() => {
    if (locked) setWizardOpen(true);
  }, [locked]);

  // Return from an OAuth round-trip inside the wizard: reopen it on THIS site.
  // Capture the id, then strip ?wiz (keep ?cloudflare/?gtm for the method resume).
  useEffect(() => {
    const w = searchParams.get('wiz');
    if (!w) return;
    setWizTarget(w);
    setWizardOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('wiz');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Keep the project store in sync with the URL — but only ever select a REAL,
  // existing project. Syncing the store to a routed-but-deleted site would fight
  // the validity fallback below (each resets the other) and spin into an infinite
  // update loop (React #185). A dead route is handled by the redirect effect.
  useEffect(() => {
    const list = projectsQuery.data?.projects;
    if (
      routeProjectId &&
      routeProjectId !== currentProjectId &&
      list?.some((p) => p.id === routeProjectId)
    ) {
      setCurrentProjectId(routeProjectId);
    }
  }, [routeProjectId, currentProjectId, projectsQuery.data, setCurrentProjectId]);
  // The current site is the single source of truth and must always be a REAL,
  // existing project. If it points at a deleted/unknown site (e.g. just removed),
  // fall back to the first remaining site (or none) so the selector and dashboard
  // never keep a stale site or its old values.
  useEffect(() => {
    const list = projectsQuery.data?.projects;
    if (!list) return;
    if (currentProjectId && !list.some((p) => p.id === currentProjectId)) {
      setCurrentProjectId(list[0]?.id ?? null);
    }
  }, [projectsQuery.data, currentProjectId, setCurrentProjectId]);
  // Never render a dead project route (e.g. a deleted site still in the URL) — that
  // shows an empty/grey screen. Once the list has settled and the routed site is
  // gone, bounce to the home redirect, which lands on a valid site (or onboarding).
  useEffect(() => {
    const list = projectsQuery.data?.projects;
    if (!routeProjectId || !list || projectsQuery.isFetching || list.length === 0) return;
    if (!list.some((p) => p.id === routeProjectId)) navigate('/dashboard', { replace: true });
  }, [routeProjectId, projectsQuery.data, projectsQuery.isFetching, navigate]);
  // Close the mobile drawer on navigation.
  // biome-ignore lint/correctness/useExhaustiveDependencies: close drawer when the path changes
  useEffect(() => setDrawer(false), [location.pathname]);

  const pathFor = (item: NavItem): string =>
    item.to ?? (activeId ? `/projects/${activeId}${item.sub ? `/${item.sub}` : ''}` : '/dashboard');

  const initials = (user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-bg text-text">
      {drawer ? (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-[90] bg-[rgba(15,21,32,0.42)] md:hidden"
          onClick={() => setDrawer(false)}
        />
      ) : null}

      {/* sidebar */}
      <aside
        className={cn(
          'fixed z-[95] flex h-screen w-[248px] flex-shrink-0 flex-col border-r border-border bg-surface transition-transform md:static md:translate-x-0',
          drawer ? 'translate-x-0 shadow-[0_0_40px_rgba(16,24,40,0.18)]' : '-translate-x-full',
        )}
      >
        <div className="flex items-center gap-2.5 px-5 pb-4 pt-[18px]">
          <span className="grid h-[30px] w-[30px] place-items-center rounded-lg bg-accent-bg">
            <ShieldCheck className="h-[18px] w-[18px] text-primary" />
          </span>
          <span className="text-[17px] font-bold tracking-[-0.02em]">Silospot</span>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-1">
          {nav.map((g) => (
            <div key={g.group} className="mb-3.5">
              <div className="px-2.5 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-3">
                {t(`nav.${g.group}`)}
              </div>
              {g.items.map((item) => {
                const to = pathFor(item);
                const Icon = item.icon;
                // Highlight only the current page. Project items resolve to
                // `/dashboard` when there is no active project (activeId=''), so
                // guard against that fallback matching every item on /dashboard.
                const isActive = item.to
                  ? location.pathname === item.to
                  : activeId
                    ? item.sub === ''
                      ? location.pathname === `/projects/${activeId}`
                      : location.pathname === to
                    : false;
                return (
                  <NavLink
                    key={item.key}
                    to={to}
                    className={cn(
                      'mb-px flex items-center gap-[11px] rounded-lg px-2.5 py-2 text-[13.5px] font-medium transition-colors',
                      isActive
                        ? 'bg-accent-bg font-semibold text-accent-strong'
                        : 'text-text-2 hover:bg-bg hover:text-text',
                    )}
                  >
                    <Icon
                      className={cn('h-[17px] w-[17px]', isActive ? 'text-primary' : 'text-text-3')}
                    />
                    <span className="min-w-0 flex-1 truncate">{t(`nav.${item.key}`)}</span>
                    {item.live ? <span className="livedot" /> : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="p-3">
          <button
            type="button"
            onClick={() => navigate('/settings/profile')}
            className="mb-2.5 flex w-full items-center gap-2.5 rounded-[10px] border border-border bg-surface p-2 text-left hover:bg-bg"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-primary to-accent-strong text-[13px] font-semibold text-white">
              {initials}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-semibold">
                {user?.name ?? t('account')}
              </span>
              <span className="block truncate text-[11px] text-text-3">{user?.email}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="mb-2.5 flex w-full items-center justify-center gap-1.5 rounded-[9px] border border-accent-border bg-accent-bg px-2 py-2.5 text-[13px] font-semibold text-accent-strong hover:brightness-95"
          >
            <Plus className="h-4 w-4" />
            {t('addSite.open')}
          </button>
        </div>
      </aside>

      {/* content */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="z-20 flex flex-shrink-0 items-center gap-3.5 border-b border-border bg-[rgba(255,255,255,0.85)] px-3.5 py-2.5 backdrop-blur md:px-[22px]">
          <button
            type="button"
            aria-label="Menu"
            onClick={() => setDrawer(true)}
            className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[9px] border border-border bg-surface text-text-2 md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

          <SiteSelect
            projects={projects}
            activeProject={activeProject}
            onSelect={(pid) => {
              setCurrentProjectId(pid);
              navigate(`/projects/${pid}`);
            }}
            onAdd={() => setWizardOpen(true)}
            placeholder={t('topbar.selectSite')}
          />

          <div className="hidden flex-1 items-center gap-2 rounded-[9px] border border-transparent bg-neutral-bg px-3 py-2 md:flex">
            <Search className="h-4 w-4 text-text-3" />
            <input
              placeholder={t('topbar.search')}
              className="w-full bg-transparent text-[13px] outline-none placeholder:text-text-3"
            />
          </div>
          <div className="flex-1 md:hidden" />

          <PeriodSelect />
          <LanguageSelect />
          <button
            type="button"
            aria-label={t('topbar.notifications')}
            className="relative grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-[9px] border border-border bg-surface text-text-2 hover:bg-bg"
          >
            <Bell className="h-[17px] w-[17px]" />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1240px] px-3.5 py-5 pb-14 md:px-[26px] md:py-6">
            {locked ? (
              <LockedDashboard resume={resume} onResume={() => setWizardOpen(true)} />
            ) : needsTracker ? (
              <NoTracker
                projectId={routeProjectId as string}
                domain={activeProject?.primaryDomain ?? ''}
              />
            ) : (
              <Outlet />
            )}
          </div>
        </main>
      </div>

      {wizardOpen && !(wizTarget && wizResumeQ.isLoading) ? (
        <OnboardingWizard
          // Re-seed when the resumed site changes (so a ?wiz return lands on the
          // right site at the right step, never a stale one).
          key={resume?.projectId ?? 'new'}
          resume={resume}
          onClose={() => {
            setWizardOpen(false);
            setWizTarget(null);
            void qc.invalidateQueries({ queryKey: ['onboarding'] });
          }}
        />
      ) : null}
    </div>
  );
}

// --- Topbar widgets --------------------------------------------------------
function SiteSelect({
  projects,
  activeProject,
  onSelect,
  onAdd,
  placeholder,
}: {
  projects: { id: string; name: string; primaryDomain: string }[];
  activeProject?: { id: string; name: string };
  onSelect: (id: string) => void;
  onAdd: () => void;
  placeholder: string;
}): ReactElement {
  const { t } = useTranslation('shell');
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 rounded-[10px] border border-border bg-surface py-1.5 pl-2.5 pr-2.5 hover:border-border-strong"
      >
        <span className="h-[7px] w-[7px] rounded-full bg-clean-fg" />
        <span className="max-w-[140px] truncate text-[13px] font-semibold">
          {activeProject?.name ?? placeholder}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-text-3" />
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-[calc(100%+6px)] z-50 w-[296px] rounded-xl border border-border bg-surface p-1.5 shadow-[0_12px_32px_rgba(16,24,40,0.14)]">
            {projects.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onSelect(p.id);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-bg',
                  p.id === activeProject?.id && 'bg-accent-bg',
                )}
              >
                <span className="h-[7px] w-[7px] rounded-full bg-clean-fg" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{p.name}</span>
                  <span className="block truncate text-[11px] text-text-3">{p.primaryDomain}</span>
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                onAdd();
                setOpen(false);
              }}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] font-semibold text-accent-strong hover:bg-bg"
            >
              <Plus className="h-4 w-4" />
              {t('addSite.open')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function PeriodSelect(): ReactElement {
  const { t } = useTranslation('analytics');
  const { period, from, to, setPeriod, setCustom } = usePeriodStore();
  const [open, setOpen] = useState(false);
  const dateInput =
    'h-8 w-full rounded-lg border border-border bg-surface px-2 text-[12.5px] text-text focus:outline-none focus:ring-1 focus:ring-accent-strong';
  return (
    <div className="relative hidden flex-shrink-0 sm:block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-[34px] items-center gap-1.5 rounded-[9px] border border-border bg-surface px-2.5 text-[12.5px] font-semibold text-text-2 hover:bg-bg"
      >
        {t(`period.${period}`)}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[200px] rounded-xl border border-border bg-surface p-1.5 shadow-[0_12px_32px_rgba(16,24,40,0.14)]">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setPeriod(p);
                  // Keep the panel open for the custom range so dates can be picked.
                  if (p !== 'custom') setOpen(false);
                }}
                className={cn(
                  'block w-full rounded-lg px-2.5 py-2 text-left text-[13px] hover:bg-bg',
                  p === period ? 'bg-accent-bg font-semibold text-accent-strong' : 'text-text-2',
                )}
              >
                {t(`period.${p}`)}
              </button>
            ))}
            {period === 'custom' ? (
              <div className="mt-1 flex flex-col gap-1.5 border-t border-border px-1.5 pb-1 pt-2">
                <label className="flex flex-col gap-1 text-[11.5px] text-text-3">
                  {t('period.from')}
                  <input
                    type="date"
                    value={from ?? ''}
                    max={to || undefined}
                    onChange={(e) => setCustom(e.target.value, to ?? e.target.value)}
                    className={dateInput}
                  />
                </label>
                <label className="flex flex-col gap-1 text-[11.5px] text-text-3">
                  {t('period.to')}
                  <input
                    type="date"
                    value={to ?? ''}
                    min={from || undefined}
                    onChange={(e) => setCustom(from ?? e.target.value, e.target.value)}
                    className={dateInput}
                  />
                </label>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function LanguageSelect(): ReactElement {
  const locale = useLocaleStore((s) => s.locale);
  const changeLocale = useChangeLocale();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Language"
        className="flex h-[34px] items-center gap-1.5 rounded-[9px] border border-border bg-surface px-2.5 hover:bg-bg"
      >
        <span className="text-[12.5px] font-bold uppercase tracking-[0.02em] text-[#3a4151]">
          {locale}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-text-3" />
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[184px] rounded-xl border border-border bg-surface p-1.5 shadow-[0_12px_32px_rgba(16,24,40,0.14)]">
            {LOCALES.map((value: Locale) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  changeLocale(value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-[13px] hover:bg-bg',
                  value === locale ? 'font-semibold text-accent-strong' : 'text-text-2',
                )}
              >
                {LOCALE_LABELS[value]}
                <span className="text-[11px] uppercase text-text-3">{value}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
